import { textContains, ValueOf } from "@bridgesplit/utils";
import { LoopMetadataTag, OracleQuote, VaultMetadataTag } from "@bridgesplit/abf-sdk";

import {
    FilterConfig,
    FilterState,
    FilterType,
    FilterReturnType,
    ExploreLoopsFilters,
    ExploreVaultsFilters,
    FilterProps,
    PluralLabelText
} from "../types/filters";
import { getLoopBorrowCapDetails } from "../api";
import { EXPLORE_LOOPS_FILTER_CONFIG, EXPLORE_VAULTS_FILTER_CONFIG } from "../constants";
import {
    TAG_FILTER_KEY,
    DEPOSIT_ASSETS_FILTER_KEY,
    BORROW_ASSETS_FILTER_KEY,
    PROTOCOLS_FILTER_KEY,
    INCLUDE_NEGATIVE_APY_FILTER_KEY,
    NEW_ONLY_FILTER_KEY
} from "../filters";
import { LendingVaultExpanded, LoopExpanded } from "../types";
import { BsMetaUtil } from "./metadata";

export function areAllFiltersEqual<T extends FilterConfig>(curr: FilterState<T>, prev: FilterState<T>) {
    for (const key of Object.keys(curr)) {
        if (!isFilterEqual(curr[key], prev[key])) return false;
    }
    return true;
}

export function matchesFilter<T extends FilterConfig>(
    config: T,
    filters: FilterState<T>,
    key: keyof T,
    value: string | string[] | boolean
): boolean {
    const type = config[key];

    if (type === FilterType.MultiSelect) {
        return matchesMultiSelectFilter(filters, key, value as string | string[]);
    }

    if (type === FilterType.InclusiveBoolean) {
        return inclusiveMatchesBooleanFilter(filters, key, value as boolean);
    }
    if (type === FilterType.Boolean) {
        return matchesBooleanFilter(filters, key, value as boolean);
    }

    // fallback to true (e.g. unrecognized type)
    return true;
}

export function matchesMultiSelectFilter<T extends FilterConfig>(
    filters: FilterState<T>,
    key: keyof T,
    value: string | string[]
): boolean {
    const enabled = filters[key].enabled;
    const condition = filters[key].condition;

    if (!enabled) return true;

    if (!(condition instanceof Set)) return false;

    if (typeof value === "string") return condition.has(value);
    return value.some((v) => condition.has(v));
}

export function inclusiveMatchesBooleanFilter<T extends FilterConfig>(
    filters: FilterState<T>,
    key: keyof T,
    value: boolean
): boolean {
    const condition = filters[key].condition;

    if (typeof value !== "boolean") return true;

    // If condition is true, include all loops (both positive and negative APY)
    // If condition is false, only include positive APY loops (exclude negative APY)
    return condition ? true : !value;
}

export function matchesBooleanFilter<T extends FilterConfig>(
    filters: FilterState<T>,
    key: keyof T,
    value: boolean
): boolean {
    const enabled = filters[key].enabled;
    const condition = filters[key].condition;

    if (!enabled) return true;

    if (typeof value !== "boolean") return true;

    // If condition is true, only include items where value is true
    // If condition is false, only include items where value is false
    return condition === value;
}

export function isFilterEqual<T extends FilterConfig>(
    curr: ValueOf<FilterState<T>>,
    prev: ValueOf<FilterState<T>>
): boolean {
    const { enabled, condition } = curr;
    const { enabled: prevEnabled, condition: prevCondition } = prev;

    if (enabled !== prevEnabled) return false;

    // Always equal if not enabled
    if (!enabled && !prevEnabled) return true;

    return isConditionEqual(condition, prevCondition);
}

export function isConditionEqual<T extends FilterConfig>(
    curr: FilterReturnType[T[keyof T]],
    prev: FilterReturnType[T[keyof T]]
): boolean {
    if (curr instanceof Set && prev instanceof Set) {
        if (curr.size !== prev.size) return false;
        for (const val of curr) {
            if (!prev.has(val)) return false;
        }
        return true;
    }
    if (typeof curr === "boolean" && typeof prev === "boolean") {
        return curr === prev;
    }
    return curr === prev;
}

export function resetFilterIfAll<T extends FilterConfig, K extends FilterReturnType[T[keyof T]]>(
    curr: K,
    check: K,
    defaultState: { enabled: boolean; condition: K }
): { enabled: boolean; condition: K } {
    const isDefault = isConditionEqual(curr, check);
    if (isDefault && defaultState.condition instanceof Set) return defaultState;
    else
        return {
            enabled: true,
            condition: curr
        };
}

export function getLoopAvailableUsd(loopExpanded: LoopExpanded, principalOracle: OracleQuote) {
    const borrowCapDetails = getLoopBorrowCapDetails(loopExpanded);
    const available = Math.min(
        loopExpanded.loopVault.principalAmountAvailable,
        borrowCapDetails?.remainingGlobalCapacity || 0
    );
    return principalOracle.getUsdAmount(available);
}

//Returns true if the loop is at capacity
export function getLoopAtCapacity(loopExpanded: LoopExpanded | undefined, principalOracle: OracleQuote | undefined) {
    if (!loopExpanded || !principalOracle) return false;
    const available = getLoopAvailableUsd(loopExpanded, principalOracle);
    return available === 0;
}

//Data function
export function getFilteredLoops(
    search: string | undefined,
    loops: LoopExpanded[] | undefined,
    filters: FilterState<ExploreLoopsFilters>
): LoopExpanded[] | undefined {
    if (!loops) return undefined;

    return loops.filter((loop) => {
        // Early return if search doesn't match
        if (search && !matchesSearch(loop, search)) {
            return false;
        }

        return matchesFilters(loop, filters);
    });
}

function matchesSearch(loop: LoopExpanded, search: string): boolean {
    const searchableTexts = [
        BsMetaUtil.getSymbol(loop.collateralToken),
        BsMetaUtil.getSymbol(loop.principalToken),
        BsMetaUtil.getName(loop.principalToken),
        BsMetaUtil.getName(loop.collateralToken),
        loop.loopVault.name
    ];
    return textContains(searchableTexts, search);
}

function matchesFilters(loop: LoopExpanded, current: FilterState<ExploreLoopsFilters>) {
    return (
        matchesFilter(EXPLORE_LOOPS_FILTER_CONFIG, current, TAG_FILTER_KEY, loop.loopVault.tags ?? []) &&
        matchesFilter(EXPLORE_LOOPS_FILTER_CONFIG, current, DEPOSIT_ASSETS_FILTER_KEY, loop.collateralToken.mint) &&
        matchesFilter(EXPLORE_LOOPS_FILTER_CONFIG, current, BORROW_ASSETS_FILTER_KEY, loop.principalToken.mint) &&
        matchesFilter(EXPLORE_LOOPS_FILTER_CONFIG, current, PROTOCOLS_FILTER_KEY, loop.loopVault.protocols ?? []) &&
        matchesFilter(EXPLORE_LOOPS_FILTER_CONFIG, current, INCLUDE_NEGATIVE_APY_FILTER_KEY, isLoopNegativeApy(loop)) &&
        matchesFilter(EXPLORE_LOOPS_FILTER_CONFIG, current, NEW_ONLY_FILTER_KEY, isLoopNew(loop))
    );
}

//Data function
export function getFilteredLendVaults(
    search: string | undefined,
    vaults: LendingVaultExpanded[] | undefined,
    filters: FilterState<ExploreVaultsFilters>
): LendingVaultExpanded[] | undefined {
    if (!vaults) return undefined;

    return vaults.filter((vault) => {
        // Early return if search doesn't match
        if (search && !matchesVaultSearch(vault, search)) {
            return false;
        }

        return matchesVaultFilters(vault, filters);
    });
}

function matchesVaultSearch(vault: LendingVaultExpanded, search: string): boolean {
    const searchableTexts = [
        vault.vaultMetadata?.name ?? "",
        BsMetaUtil.getSymbol(vault.principalMetadata),
        BsMetaUtil.getName(vault.principalMetadata),
        vault.vault.address
    ];
    return textContains(searchableTexts, search);
}

function matchesVaultFilters(vault: LendingVaultExpanded, current: FilterState<ExploreVaultsFilters>) {
    return (
        matchesFilter(EXPLORE_VAULTS_FILTER_CONFIG, current, TAG_FILTER_KEY, vault.vaultMetadata?.tags ?? []) &&
        matchesFilter(EXPLORE_VAULTS_FILTER_CONFIG, current, DEPOSIT_ASSETS_FILTER_KEY, vault.vault.principalMint)
    );
}

export function hasActiveFilters<T extends FilterConfig>(filterProps: FilterProps<T>): boolean {
    return Object.values(filterProps.filters).some((filter) => filter.enabled);
}

function isLoopNegativeApy(loop: LoopExpanded): boolean {
    return (loop.loopVault.wAvgApy ?? -1) < 0 || (loop.loopVault.maxLeveragedApyPct ?? -1) < 0;
}

export function isLoopNew(loop: LoopExpanded | undefined): boolean {
    return loop?.loopVault.metadataTags?.includes(LoopMetadataTag.New) ?? false;
}

export function isLeverageLoop(loop: LoopExpanded | undefined): boolean {
    return loop?.loopVault.metadataTags?.includes(LoopMetadataTag.Leverage) ?? false;
}

export function isLoopComingSoon(loop: LoopExpanded | undefined): boolean {
    return loop?.loopVault.metadataTags?.includes(LoopMetadataTag.ComingSoon) ?? false;
}

export function isLendVaultComingSoon(vault: LendingVaultExpanded | undefined): boolean {
    return vault?.vaultMetadata?.tags?.includes(VaultMetadataTag.ComingSoon) ?? false;
}

export function getLoopNotifyFormUrl(loop: LoopExpanded | undefined): string | undefined {
    return loop?.loopVault.metadata?.notifyFormUrl;
}

export function getLendVaultNotifyFormUrl(vault: LendingVaultExpanded | undefined): string | undefined {
    return vault?.vaultMetadata?.notifyFormUrl;
}

export function getLoopComingSoonBanner(loop: LoopExpanded | undefined): string | undefined {
    const banner = loop?.loopVault.metadata?.comingSoonBanner;
    return banner?.trim() ? banner : undefined;
}

export function getLendVaultComingSoonBanner(vault: LendingVaultExpanded | undefined): string | undefined {
    const banner = vault?.vaultMetadata?.comingSoonBanner;
    return banner?.trim() ? banner : undefined;
}

export function getLoopFilterLabel({
    allSelected,
    size,
    labelOptions,
    isMobile
}: {
    allSelected: boolean;
    size: number;
    labelOptions: PluralLabelText;
    isMobile?: boolean;
}): string {
    if (isMobile) {
        if (allSelected) return labelOptions.mobile?.all ?? size.toString();
        const label = size > 1 ? labelOptions.mobile?.multi : labelOptions.mobile?.single;
        return label ? `${size} ${label}` : size.toString();
    }

    if (allSelected) return labelOptions.desktop.all;
    const label = size > 1 ? labelOptions.desktop.multi : labelOptions.desktop.single;
    return label ? `${size} ${label}` : size.toString();
}
